import { useRef } from 'react';
import { Button, CenterScreenModal } from '../atoms'
import { 
  useContractDetailsContext,
  useErrorHandlerContext,
  useOpeningElementsControllerContext,
  useUserLanguage,
} from '@/customHooks'
import { Col, Row } from 'react-grid-system';
import {
  CreateRentChargeInput,
  useCreateRentChargeFromContractMutation,
  useUpdateRentChargeFromContractMutation,
} from '@/graphql/generated';
import { useAddRentChargeFormModalContext } from '@/customHooks/useAddRentChargeFormModalContext';
import { AddRentChargeFormControlled } from '../organism';

const textsData = {
  en: {
    title: 'Add an Rent Charge',
    confirmation: {
      create: {
        title: 'Rent charge has been created',
        description: (name: string) => `Rent charge ${name} has been created succesfully`,
      },
      update: {
        title: 'Rent charge has been updated',
        description: (name: string) => `Rent charge ${name} has been updated succesfully`,
      },
    },
    button: {
      confirm: 'Confirm',
      cancel: 'Cancel'
    }
  },
  es: {
    title: 'Crear una Cargo',
    confirmation: {
      create: {
        title: 'Cargo creada con éxito',
        description: (name: string) => `El cargo ${name} fue creada con éxito`,
      },
      update: {
        title: 'Cargo actualizada con exito',
        description: (name: string) => `El cargo ${name} fue actualizada con éxito`,
      },
    },
    button: {
      confirm: 'Aceptar',
      cancel: 'Volver'
    }
  }
}

export const AddRentChargeFormModal = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const rentChargeDataRef = useRef<CreateRentChargeInput | null>(null)
  
  const [createRentCharge, { loading: createLoading }] = useCreateRentChargeFromContractMutation()
  const [updateRentCharge, { loading: updateLoading }] = useUpdateRentChargeFromContractMutation()

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const { rentChargeData, isOpen, closeModal, } = useAddRentChargeFormModalContext()

  const { contract, setContract } = useContractDetailsContext()
  
  const { open } = useErrorHandlerContext()

  const OnChangeHandle = (rentChargeData: CreateRentChargeInput) => {
    rentChargeDataRef.current = rentChargeData
  }

  const onConfirmHandle = () => {
    if (!rentChargeDataRef.current) return
    
    if (rentChargeData === null) {
      createRentCharge({ variables: {
        contractId: contract?.id ?? '',
        amountValue: rentChargeDataRef.current.amountValue,
        description: rentChargeDataRef.current.description,
        rentChargeTypeOption: rentChargeDataRef.current.rentChargeType,
        executionDate: rentChargeDataRef.current.executionDate,
      }})
      .then((response) => {
        setContract(undefined)
        closeModal()
        confirmDialogModalOpen({
          title: texts.confirmation.create.title,
          description: texts.confirmation.create.description(response.data?.createRentChargeFromContract.rentChargeTypeOption ?? ''),
        })
      })
      .catch((error) => {
        closeModal()
        open(error)
      })
    } 
    else {
      updateRentCharge({ variables: {
        contractId: contract?.id ?? '',
        rentChargeId: rentChargeData.id,
        amountValue: rentChargeDataRef.current.amountValue,
        description: rentChargeDataRef.current.description,
        rentChargeTypeOption: rentChargeDataRef.current.rentChargeType,
        executionDate: rentChargeDataRef.current.executionDate,
      }})
      .then((response) => {
        setContract(undefined)
        closeModal()
        confirmDialogModalOpen({
          title: texts.confirmation.update.title,
          description: texts.confirmation.update.description(response.data?.updateRentChargeFromContract.rentChargeTypeOption ?? '')
        })
      })
      .catch((error) => {
        closeModal()
        open(error)
      })  
    }
  }
  
  return (
    <CenterScreenModal
      isOpen={isOpen}
      onClose={closeModal}
    >
      <Row>
        <Col>
          <h2>{texts.title}</h2>
        </Col>
      </Row>
      <AddRentChargeFormControlled
        defaultData={
          rentChargeData
          ? ({
            amountValue: rentChargeData.amountValue,
            description: rentChargeData.description,
            rentChargeType: rentChargeData.rentChargeTypeOption,
            executionDate: rentChargeData.executionDate
          })
          : undefined
        }
        onChange={OnChangeHandle}
      />
      <br />
      <Row justify="center" gutterWidth={32}>
        <Col xs="content">
          <Button secondary onClick={() => closeModal()} disabled={!!updateLoading || !!createLoading}>
            {texts.button.cancel}
          </Button>
        </Col>
        <Col xs="content">
          <Button
            onClick={() => onConfirmHandle()}
            isLoading={!!updateLoading || !!createLoading}
          >
            {texts.button.confirm}
          </Button>
        </Col>
      </Row>
    </CenterScreenModal>
  )
}
